import React, { useMemo, useState, useEffect, useRef } from 'react'
import { Card, CardContent, Button, Modal } from 'semantic-ui-react'
import { AppButton, AppPrimaryButton } from 'elements'
import {
    AppTable,
    FlexBox,
    FlexRowBetween,
    FlexRowWrap,
} from 'components'
import { DateUtility, DateFormat, CommonUtility, ReportService } from 'utility'
import { Clock, PencilSimple, DotsThreeVertical, Envelope, Trash, X } from 'phosphor-react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { useNotification } from 'hooks/toast'

const ActionDropdownContainer = styled.div`
    position: relative;
    display: inline-block;
`

const DropdownMenu = styled.div`
    position: absolute;
    top: 100%;
    right: 0;
    background: white;
    border: 1px solid #e8e9e9;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    min-width: 200px;
    z-index: 1000;
    margin-top: 8px;
    padding: 4px 0;
`

const MenuItem = styled.div`
    padding: 12px 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 12px;
    transition: background-color 0.2s ease;
    font-size: 14px;
    color: ${props => props.danger ? '#dc3545' : '#333'};

    &:hover {
        background-color: #f8f9fa;
    }

    &:last-child {
        border-top: 1px solid #f0f0f0;
    }
`

const ActionBoxes = ({
    item,
    onRemove,
    onEdit,
    onSendEmailReminder,
}) => {
    const [dropdownOpen, setDropdownOpen] = useState(false)
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const dropdownRef = useRef(null)

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    const handleEdit = () => {
        onEdit(item)
        setDropdownOpen(false)
    }

    const handleDeleteClick = () => {
        setDeleteConfirmOpen(true)
        setDropdownOpen(false)
    }

    const handleDeleteConfirm = () => {
        onRemove(item.TaskId)
        setDeleteConfirmOpen(false)
    }

    const { errorToast } = useNotification()
    const inspectionId =
        item.AuditUniqueId ||
        item.TemplateData?.AuditTemplateId ||
        item.AuditTemplateId ||
        ''

    const handleEmailReminder = () => {
        onSendEmailReminder(item)
        setDropdownOpen(false)
    }

    const openInspection = () => {
        if (!inspectionId) {
            errorToast(
                'Could not open this inspection. The audit id is missing.',
            )
        }
    }

    return (
        <>
            <FlexBox className="gap-2 align-items-center">
                {inspectionId ? (
                    <Link to={`/app/templates/inspection/${inspectionId}`}>
                        <AppButton>
                            Resume Inspection
                        </AppButton>
                    </Link>
                ) : (
                    <AppButton onClick={openInspection}>
                        Resume Inspection
                    </AppButton>
                )}
                <ActionDropdownContainer ref={dropdownRef}>
                    <Button
                        icon
                        basic
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                        style={{ padding: '8px', minWidth: 'auto' }}
                    >
                        <DotsThreeVertical size={20} />
                    </Button>
                    {dropdownOpen && (
                        <DropdownMenu>
                            <MenuItem onClick={handleEdit}>
                                <PencilSimple size={16} />
                                <span>Edit</span>
                            </MenuItem>
                            <MenuItem onClick={handleEmailReminder}>
                                <Envelope size={16} />
                                <span>Send Email Reminder</span>
                            </MenuItem>
                            <MenuItem onClick={handleDeleteClick} danger>
                                <Trash size={16} />
                                <span>Delete</span>
                            </MenuItem>
                        </DropdownMenu>
                    )}
                </ActionDropdownContainer>
            </FlexBox>
            <Modal open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} size="tiny" className="model">
                <Modal.Header>
                    <FlexRowBetween>
                        <span>Delete</span>
                        <X className="pointer" size={32} onClick={() => setDeleteConfirmOpen(false)} />
                    </FlexRowBetween>
                </Modal.Header>
                <Modal.Content>
                    <p>Are you sure you want to delete this item?</p>
                </Modal.Content>
                <Modal.Actions>
                    <AppButton onClick={() => setDeleteConfirmOpen(false)}>
                        Cancel
                    </AppButton>
                    <AppPrimaryButton onClick={handleDeleteConfirm} style={{ backgroundColor: '#dc3545' }}>
                        Delete
                    </AppPrimaryButton>
                </Modal.Actions>
            </Modal>
        </>
    )
}

const DueTemplatesCard = ({
    item,
    onRemove,
    onEdit,
    onSendEmailReminder,
}) => {
    return (
        <Card fluid>
            <CardContent>
                <FlexRowBetween>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <h4 title={item.AuditName} style={{ margin: 0 }}>
                            {CommonUtility.truncateString(item.AuditName, 20)}
                        </h4>
                        {item.StatusName && (
                            <span
                                style={{
                                    backgroundColor: 'teal',
                                    color: 'white',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    display: 'inline-block',
                                    marginLeft: '8px',
                                    fontWeight: 500,
                                }}
                            >
                                {item.StatusName}
                            </span>
                        )}
                    </div>
                </FlexRowBetween>
                <div className="mt-2">
                    <div
                        className="align-items-center d-flex"
                        style={{ marginBottom: '8px' }}
                    >
                        <Clock className="me-1" size={16} />
                        <strong>Create Date:</strong>{' '}
                        <span>
                            {item.CreatedOn
                                ? DateUtility.dateToLocalISO(
                                      new Date(item.CreatedOn),
                                      DateFormat.date,
                                  )
                                : '-'}
                        </span>
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                        <strong>Created By:</strong>{' '}
                        <span>
                            {item.CreatedByName || item.CreatedBy || '-'}
                        </span>
                    </div>
                    <div
                        className="align-items-center d-flex"
                        style={{ marginBottom: '8px' }}
                    >
                        <Clock className="me-1" size={16} />
                        <strong>Due Date:</strong>{' '}
                        <span>
                            {item.TaskDue
                                ? DateUtility.dateToLocalISO(
                                      new Date(item.TaskDue),
                                      DateFormat.date,
                                  )
                                : '-'}
                        </span>
                    </div>
                </div>
                <FlexRowWrap className="justify-content-end mt-2">
                    <ActionBoxes
                        item={item}
                        onRemove={onRemove}
                        onEdit={onEdit}
                        onSendEmailReminder={onSendEmailReminder}
                    />
                </FlexRowWrap>
            </CardContent>
        </Card>
    )
}

export const DueTemplatesList = ({
    data,
    filter,
    pageChanged,
    totalCount,
    onRemove,
    onEdit,
    onViewReport,
    onReopen,
    onReject,
}) => {
    const handleReject = (item, comment) => {
        onReject(item, comment)
    }
    const { successToast, errorToast } = useNotification()

    const handleSendEmailReminder = async (item) => {
        try {
            const payload = {
                AuditUniqueId: item.AuditUniqueId || item.TemplateData?.AuditTemplateId || item.AuditTemplateId,
            }
            await ReportService.sendEmailDueTemplateReminder(payload)
            successToast('Email reminder sent successfully')
        } catch (error) {
            console.error('Error sending email reminder:', error)
            errorToast('Failed to send email reminder. Please try again.')
        }
    }

    const columns = useMemo(
        () => [
            {
                title: 'Audit Name',
                key: 'AuditName',
                render: item => (
                    <span title={item.AuditName}>
                        {item.AuditName || '-'}
                    </span>
                ),
            },
            {
                title: 'Create Date',
                render: item => (
                    <span>
                        {item.CreatedOn
                            ? DateUtility.dateToLocalISO(
                                  new Date(item.CreatedOn),
                                  DateFormat.date,
                              )
                            : '-'}
                    </span>
                ),
            },
            {
                title: 'Created By',
                key: 'CreatedByName',
                render: item => (
                    <span>{item.CreatedByName || item.CreatedBy || '-'}</span>
                ),
            },
            {
                title: 'Due Date',
                render: item => (
                    <span>
                        {item.TaskDue
                            ? DateUtility.dateToLocalISO(
                                  new Date(item.TaskDue),
                                  DateFormat.date,
                              )
                            : '-'}
                    </span>
                ),
            },
            {
                title: 'Status Name',
                render: item => (
                    <span
                        style={{
                            backgroundColor: 'teal',
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            display: 'inline-block',
                            fontWeight: 500,
                        }}
                    >
                        {item.StatusName || '-'}
                    </span>
                ),
            },
            {
                title: 'Actions',
                key: 'Actions',
                render: item => (
                    <FlexBox className="justify-content-center">
                        <ActionBoxes
                            item={item}
                            onRemove={onRemove}
                            onEdit={onEdit}
                            onSendEmailReminder={handleSendEmailReminder}
                        />
                    </FlexBox>
                ),
            },
        ],
        [data, totalCount, filter],
    )

    return (
        <>
            <AppTable
                data={data}
                columns={columns}
                pageChanged={pageChanged}
                filter={filter}
                totalCount={totalCount}
                mobileCard={
                    <DueTemplatesCard
                        onEdit={onEdit}
                        onRemove={onRemove}
                        onViewReport={onViewReport}
                        onReopen={onReopen}
                        onReject={handleReject}
                        onSendEmailReminder={handleSendEmailReminder}
                    />
                }
            />
        </>
    )
}
